import { useMemo } from 'react'
import { useLocale, useTranslations } from 'use-intl'
import { usePublicCategories } from '~/features/categories/hooks/use-public-categories'
import { Card } from '@heroui/react'
import { LocalizedLink } from '~/components/common/localized-link'
import { Breadcrumb } from '~/components/common/Breadcrumb'
import { getLocalizedCategoryName } from '~/features/categories/utils/localization'
import { getCategoryImageDir } from '~/features/categories/utils/image-path'

export function CategoriesIndexPage() {
  const t = useTranslations('HomePage.guides')
  const locale = useLocale()
  const { data: dbCategories } = usePublicCategories()

  // Sort by weight (popularity) descending
  const sortedCategories = useMemo(() => {
    if (!dbCategories) return []
    return [...dbCategories].sort((a: any, b: any) => (b.weight ?? 0) - (a.weight ?? 0))
  }, [dbCategories])

  return (
    <main className="container mx-auto px-4 py-8 md:py-12">
      <Breadcrumb items={[
        { label: 'TabeTabi', href: '/' },
        { label: t('categoriesTitle') },
      ]} />

      <div className="text-center mb-10">
        <h1 className="text-3xl md:text-4xl font-bold mb-3">{t('categoriesTitle')}</h1>
        <p className="text-default-500 max-w-xl mx-auto">
          {locale === 'zh-CN' ? '探索日本各类美食的历史、文化与就餐指南。'
            : locale === 'zh-TW' ? '探索日本各類美食的歷史、文化與用餐指南。'
            : locale === 'ja' ? '日本の多彩なグルメの歴史、文化、食事ガイドを探索しましょう。'
            : locale === 'ko' ? '일본 다양한 미식의 역사, 문화, 식사 가이드를 탐색해보세요.'
            : 'Explore the history, culture, and dining etiquette of Japanese cuisine categories.'}
        </p>
      </div>
      
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4 md:gap-5">
        {sortedCategories.map((cat: any) => {
          const localizedName = getLocalizedCategoryName(cat, locale)
          const dirName = getCategoryImageDir(cat.en)
          const imgUrl = `/images/guides/categories/${dirName}/hero.png`
          
          return (
            <LocalizedLink key={cat.id} to={`/categories/${cat.id}`} className="block group cursor-pointer">
              <Card className="border-none bg-transparent shadow-none">
                <div className="relative overflow-hidden rounded-xl aspect-[4/5]">
                  <img
                    src={imgUrl}
                    alt={localizedName}
                    className="object-cover w-full h-full group-hover:scale-110 transition-transform duration-500"
                    loading="lazy"
                    onError={(e) => {
                      const target = e.target as HTMLImageElement
                      target.onerror = null // Prevent infinite loop
                      // Use an inline SVG data URI (light gray placeholder) so it never 404s
                      target.src = 'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIxMDAlIiBoZWlnaHQ9IjEwMCUiPjxyZWN0IHdpZHRoPSIxMDAlIiBoZWlnaHQ9IjEwMCUiIGZpbGw9IiNlMmU4ZjAiIC8+PC9zdmc+'
                    }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent z-10" />
                  <div className="absolute bottom-3 left-3 right-3 z-20">
                    <p className="text-white font-bold text-base md:text-lg drop-shadow-md">{localizedName}</p>
                  </div>
                </div>
              </Card>
            </LocalizedLink>
          )
        })}
      </div>
    </main>
  )
}
